import { useCallback, useMemo } from 'react'
import { CustomSelectSectionSearchbarDropDown, CustomSelectSectionsProps } from '../../../forms/CustomSelect/CustomSelectSections'
import { Modify } from '../../../utils/globals/types'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { faFileArrowUp, faThumbtack } from '@fortawesome/pro-light-svg-icons'
import useProjectsAndOpportunitiesDict from '../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../utils/globals/dictionary'
import { CustomSelectBase } from '../../../forms/CustomSelect'
import { DisplayTextFuncTy } from '../../../forms/CustomSelect/types'
import { projectTypesInversed } from '@teinor/erp/types/company/customer/customerProject'

type CustomSelectSectionProjectsProps = Modify<CustomSelectSectionsProps, CustomSelectSectionProjectsLogicProps & {
    items?: never
}>


const CustomSelectSectionProjects: React.FC<CustomSelectSectionProjectsProps> = ({ projects, ...rest }) => {
    const { projectsSectionItems } = useCustomSelectSectionProjectsLogic({ projects })
    return (
        <CustomSelectSectionSearchbarDropDown items={projectsSectionItems} onChange={rest.onChange as any} value={''} {...rest} />
    )
}

interface CustomSelectSectionProjectsLogicProps {
    projects: TwinDictionary
}

const useCustomSelectSectionProjectsLogic = ({ projects }: CustomSelectSectionProjectsLogicProps) => {
    const { parseSearchProjectsToSections } = useProjectsAndOpportunitiesDict()
    const { projectsSectionItems } = useMemo(() => {
        return parseSearchProjectsToSections(projects || {})
    }, [projects, parseSearchProjectsToSections])

    return { projectsSectionItems }
}

type CustomSelectAndCreateSectionProjectsProps = Modify<CustomSelectSectionsProps, {
    projects: TwinDictionary
    extraMainSections?: TwinDictionary[]
    orderByOpp?: true | undefined
    items?: never
    label?: never
}>

export const DropDownSearchBarAndCreateSectionProjects: React.FC<CustomSelectAndCreateSectionProjectsProps> = ({ projects, extraMainSections, orderByOpp, ...rest }) => {
    const { projectsSectionItems } = useCustomSelectAndCreateSectionProjectsLogic({ projects, extraMainSections, orderByOpp, customerProjectId: rest.value || '' })
    return (
        <CustomSelectSectionSearchbarDropDown items={projectsSectionItems} onChange={rest.onChange as any} value={rest.value || ''} {...rest} />
    )
}

export const CustomSelectAndCreateSectionProjects: React.FC<CustomSelectAndCreateSectionProjectsProps> = ({ projects, extraMainSections, orderByOpp, ...rest }) => {
    const { projectsSectionItems, displayTextFunc, translations } = useCustomSelectAndCreateSectionProjectsLogic({ projects, extraMainSections, orderByOpp, customerProjectId: rest.value || '' })
    return (
        <CustomSelectBase {...rest} label={translations} items={projectsSectionItems} DropDownComponent={CustomSelectSectionSearchbarDropDown} displayTextFunc={displayTextFunc} />
    )
}

interface CustomSelectAndCreateSectionProjectsLogicProps {
    projects: TwinDictionary
    extraMainSections?: TwinDictionary[]
    orderByOpp?: true | undefined
    customerProjectId: number | string
}

const useCustomSelectAndCreateSectionProjectsLogic = ({ projects, extraMainSections, orderByOpp, customerProjectId }: CustomSelectAndCreateSectionProjectsLogicProps) => {
    const { t } = useTwinTranslation()
    const dictCustomerProjects = useMemo(() => dictionaryComplexFromJsonArr(projects), [projects])
    const { parseSearchProjectsToSections } = useProjectsAndOpportunitiesDict()
    const isExtraMainSections = useMemo(() => extraMainSections ?? [], [extraMainSections])

    const projectsOppTrans = t('projectsAndOpp', 'Proyectos/Oportunidades')
    const projectTrans = t('project', 'Proyecto')
    const oppTrans = t('opportunity', 'Oportunidad')
    const othersTrans = t('actions', 'Acciones')
    const mainOtherTrans = t('createProject', 'Crear proyecto')
    const mainOtherTrans2 = t('createOpp', 'Crear oportunidad')
    const mainProjectsSections: TwinDictionary[] = useMemo(() => [{ value: 0, text: mainOtherTrans, hiddenText: mainOtherTrans, icon: faFileArrowUp, className: 'section_items_background' }, { value: -1, text: mainOtherTrans2, hiddenText: mainOtherTrans2, icon: faThumbtack, className: 'section_items_background' }, ...isExtraMainSections], [mainOtherTrans, mainOtherTrans2, isExtraMainSections])

    const { projectsSectionItems, indexProjects} = useMemo(() => {
        const { projectsSectionItems, indexProjects } = parseSearchProjectsToSections(projects, orderByOpp)
        projectsSectionItems.splice(0, 0, ...mainProjectsSections as any)
        projectsSectionItems.splice(0, 0, { value: "", text: othersTrans, isSection: true })
        return { projectsSectionItems, indexProjects }
    }, [projects, parseSearchProjectsToSections, othersTrans, mainProjectsSections, orderByOpp])

    const displayTextFunc: DisplayTextFuncTy = useCallback((_items, value) => {
        return indexProjects[value]?.text || indexProjects[value as any]
    }, [indexProjects])

    const translations = useMemo(() => {
        const projectType = dictCustomerProjects[customerProjectId]?.projectType
        let labelText = projectsOppTrans
        if (projectType || projectType === 0) {
            if (projectType === projectTypesInversed['project']) {
                labelText = projectTrans
            } else {
                labelText = oppTrans
            }
        }
        return labelText
    }, [customerProjectId, dictCustomerProjects, projectsOppTrans, projectTrans, oppTrans])

    return { projectsSectionItems, displayTextFunc, translations }
}

export default CustomSelectSectionProjects